import { isValidSegment, type PermissionId, type PermissionSubject } from './permission-id';

export interface ResourceDefinition {
  /** Actions this resource supports. Standard ones or custom verbs, kebab-case. */
  readonly actions: readonly string[];
  /** Short developer-facing description; the UI label lives in the web i18n files. */
  readonly description?: string;
}

export interface PermissionDefinition {
  readonly id: PermissionId;
  readonly module: string;
  readonly resource: string;
  readonly action: string;
  readonly subject: PermissionSubject;
  readonly description?: string;
}

/** Every permission id a catalog declares, as a string literal union. */
export type CatalogPermissionId<M extends string, R extends Record<string, ResourceDefinition>> = {
  [K in keyof R & string]: `${M}.${K}.${R[K]['actions'][number]}`;
}[keyof R & string];

export interface PermissionCatalog<Id extends PermissionId = PermissionId> {
  readonly module: string;
  readonly permissions: readonly PermissionDefinition[];
  readonly ids: readonly Id[];
}

export class InvalidPermissionCatalogError extends Error {
  constructor(message: string) {
    super(message);
    this.name = InvalidPermissionCatalogError.name;
  }
}

/**
 * Declares the permissions a module contributes. The core and every plugin call this once
 * and hand the result to a `PermissionRegistry`.
 *
 * @example
 * ```ts
 * export const corePermissions = definePermissions({
 *   module: 'core',
 *   resources: { organization: { actions: ['view', 'create', 'update', 'delete'] } },
 * });
 * ```
 */
export function definePermissions<
  const M extends string,
  const R extends Record<string, ResourceDefinition>,
>(input: { module: M; resources: R }): PermissionCatalog<CatalogPermissionId<M, R> & PermissionId> {
  const { module, resources } = input;
  if (!isValidSegment(module)) {
    throw new InvalidPermissionCatalogError(`Invalid module name "${module}".`);
  }

  const permissions: PermissionDefinition[] = [];
  for (const [resource, definition] of Object.entries(resources)) {
    if (!isValidSegment(resource)) {
      throw new InvalidPermissionCatalogError(`Invalid resource name "${module}.${resource}".`);
    }
    if (definition.actions.length === 0) {
      throw new InvalidPermissionCatalogError(`Resource "${module}.${resource}" has no actions.`);
    }
    const seen = new Set<string>();
    for (const action of definition.actions) {
      if (!isValidSegment(action)) {
        throw new InvalidPermissionCatalogError(
          `Invalid action "${action}" on "${module}.${resource}".`,
        );
      }
      if (seen.has(action)) {
        throw new InvalidPermissionCatalogError(
          `Duplicate action "${action}" on "${module}.${resource}".`,
        );
      }
      seen.add(action);
      permissions.push({
        id: `${module}.${resource}.${action}`,
        module,
        resource,
        action,
        subject: `${module}.${resource}`,
        ...(definition.description === undefined ? {} : { description: definition.description }),
      });
    }
  }

  return {
    module,
    permissions,
    ids: permissions.map((permission) => permission.id) as (CatalogPermissionId<M, R> &
      PermissionId)[],
  };
}
